import type { AuditModule, Id, ViaChannel } from './common';
import type { Direction, EntryStatusFilter } from './finance';
import type {
  AssistantScope, OnboardingState, OnboardingStep, PersonaAppearance, PersonaFocus, PersonaPersonality,
} from './persona';
export type { OnboardingStep } from './persona';
import type { SharingMode } from './sharing';

// The assistant's shared contract (spec §5). This part serves the capability guard; the admin part follows.

/** The S1–S3 object types: the only ones a briefing chip or a nudge ref may carry until S5 L4 gives the S5 types
 *  their source checkers (S3 `L4_SOURCE_CHECKERS` fails closed on a type it does not know). */
export const ASSISTANT_CORE_OBJECT_TYPES = ['task', 'note', 'project', 'objective', 'priority', 'partner', 'user'] as const;
/** S5 §6.2 (P5, D15): the rows Hana writes in every module, and the evidence `document` a batch reads (L2 P27). */
export const ASSISTANT_S5_OBJECT_TYPES =
  ['finance_entry', 'stakeholder', 'participant', 'contract', 'contact', 'metric', 'review', 'document'] as const;
/** The objects an assistant action can point at. */
export const ASSISTANT_OBJECT_TYPES = [...ASSISTANT_CORE_OBJECT_TYPES, ...ASSISTANT_S5_OBJECT_TYPES] as const;
export type AssistantObjectType = (typeof ASSISTANT_OBJECT_TYPES)[number];
export type AssistantObjectRef = { type: AssistantObjectType; id: Id };
/** What an object link in Hana's text may name (`[label](obj:type:id)`): the object types, plus `work` = "a Workplace
 *  item, task or note", for a model that only knows the item is on the board. Chips and the resolver only; the
 *  capability guard and the provenance `objects` column keep `ASSISTANT_OBJECT_TYPES`. */
export const ASSISTANT_LINK_TYPES = [...ASSISTANT_OBJECT_TYPES, 'work'] as const;
export type AssistantLinkType = (typeof ASSISTANT_LINK_TYPES)[number];
export type AssistantLinkRef = { type: AssistantLinkType; id: Id };
/** The link types that name a Workplace item; their resolved row carries `itemType`. */
export const ASSISTANT_ITEM_LINK_TYPES = ['task', 'note', 'work'] as const satisfies readonly AssistantLinkType[];

export const ASSISTANT_ACTION_STATUSES =
  ['pending', 'executed', 'undone', 'rejected', 'superseded', 'expired', 'dismissed'] as const;
export type AssistantActionStatus = (typeof ASSISTANT_ACTION_STATUSES)[number];

/** Codes the guard adds to the error envelope (spec §5.2–§5.4). NOT_FOUND, FORBIDDEN, VALIDATION, REQUEST_CONFLICT are the shared ones. */
export const ASSISTANT_ERROR_CODES = ['UNKNOWN_CAPABILITY', 'ACTION_CLOSED', 'ACTION_EXPIRED', 'ACTION_STALE', 'ACTION_UNDONE', 'NO_EDIT',
  'ASSISTANT_DISABLED', 'ASSISTANT_READ_ONLY', 'UNDO_UNAVAILABLE', 'UNDO_EXPIRED', 'UNDO_STALE',
  'ASSISTANT_QUOTA', 'TURN_RUNNING', 'PROVIDER_UNAVAILABLE', 'TURN_INTERRUPTED', 'TURN_TIMEOUT',
  // S3 L2: a nudge action whose object is gone (§7.5), `work.nudge`'s refusals (§7.7), the schedule limits (§7.6).
  'NUDGE_STALE', 'NUDGE_TOO_SOON', 'NUDGE_LIMIT', 'NUDGE_SELF', 'RECIPIENT_CANNOT_SEE', 'WATCH_LIMIT',
  // S3 L3: "Soạn lại" on a past period, past the three a day (D121), and out of time before it started (D159).
  'BRIEFING_STALE', 'BRIEFING_RECOMPOSE_LIMIT', 'BRIEFING_BUSY',
  // S3 L4: the personal (200) and company (300) memory limits (D173).
  'MEMORY_LIMIT',
  // S5 D11: a write's amount the person did not say (§6.4).
  'AMOUNT_NOT_SAID',
  // S7 §11: a level or a finance limit above the admin's ceiling ("Admin giới hạn ở …"), 422.
  'AUTONOMY_ABOVE_CEILING',
  // S7 §8.2 (D46): a call or a card action of a background run that was stopped (409).
  'RUN_CANCELLED',
  // S7 §11 L3: missions off (409), 10 live missions (409), a plan over 19 steps or 3 levels (422), a mission out of
  // tokens (a run's end), `mission.complete` with open steps (a tool error), an answer with no question (409), "Chạy
  // ngay" while a run is live (409).
  'MISSIONS_DISABLED', 'MISSION_LIMIT', 'PLAN_TOO_LARGE', 'MISSION_BUDGET', 'MISSION_STEPS_OPEN', 'MISSION_NOT_WAITING',
  'RUN_ALIVE',
  // S7 §11 L4: 20 orders on (409), "Chạy thử ngay" on an order that is off (409, P18), a branch order with no branch
  // (422, P17: 027's `ORDER_BRANCH_MISSING` trigger).
  'ORDER_LIMIT', 'ORDER_OFF', 'ORDER_BRANCH_MISSING',
  // S9 §6.5: a fourth contact (nudge or progress ask) from one sender to one person in a business day (429).
  'NUDGE_PAIR_LIMIT'] as const;
export type AssistantErrorCode = (typeof ASSISTANT_ERROR_CODES)[number];

// The admin part (spec §9.1): AI providers and their models, as the "Quản trị AI" page reads and writes them.

/** `fake` is accepted only when the server allows it (NODE_ENV=test, plan D24). */
export const AI_PROVIDER_KINDS = ['anthropic', 'openai_compatible', 'fake'] as const;
export type AiProviderKind = (typeof AI_PROVIDER_KINDS)[number];
export type AiModelDTO = { id: Id; providerId: Id; modelKey: string; displayName: string; allowed: boolean; sortOrder: number };
/** The key itself never travels: only whether one is stored and its last 4 characters. */
export type AiProviderDTO = { id: Id; name: string; kind: AiProviderKind; baseUrl: string; enabled: boolean;
  keySet: boolean; keyLast4: string | null; models: AiModelDTO[]; version: number; createdAt: string; updatedAt: string };
export type AiProviderListDTO = { secretKeyConfigured: boolean; allowFake: boolean; providers: AiProviderDTO[] };
export type AiModelInput = { id?: Id; modelKey: string; displayName: string; allowed: boolean };
export type AiProviderCreateBody = { name: string; kind: AiProviderKind; baseUrl: string; apiKey?: string; enabled?: boolean;
  models: AiModelInput[] };
/** `apiKey` absent keeps the key, a string replaces it, null clears it. `models` replaces the list (order = sort order). */
export type AiProviderPatchBody = { version: number; name?: string; kind?: AiProviderKind; baseUrl?: string;
  apiKey?: string | null; enabled?: boolean; models?: AiModelInput[] };
export const AI_CONNECTION_CODES = ['PROVIDER_AUTH', 'PROVIDER_REJECTED', 'PROVIDER_UNAVAILABLE', 'NO_KEY', 'NO_MODEL',
  'AI_KEY_UNREADABLE'] as const;
export type AiConnectionCode = (typeof AI_CONNECTION_CODES)[number];
/** "Kiểm tra kết nối": a latency, or a code. The provider's own error text never reaches the page (plan D15, D23). */
export type AiConnectionTestDTO = { ok: true; latencyMs: number } | { ok: false; code: AiConnectionCode };
/** "Lấy danh sách model": one row the provider lists; nothing is saved until the admin saves the provider. */
export type AiDiscoveredModel = { modelKey: string; displayName: string };
/** The provider's models, normalized (trimmed, deduped, sorted, at most 500), or a code of the connection-test family. */
export type AiModelDiscoveryDTO = { ok: true; models: AiDiscoveredModel[] } | { ok: false; code: AiConnectionCode };
export const AI_CONNECTION_MESSAGE: Record<AiConnectionCode, string> = {
  PROVIDER_AUTH: 'Sai API key hoặc key không có quyền',
  PROVIDER_REJECTED: 'Nhà cung cấp từ chối yêu cầu — kiểm tra model id và base URL',
  PROVIDER_UNAVAILABLE: 'Không kết nối được nhà cung cấp (hết thời gian hoặc lỗi máy chủ)',
  NO_KEY: 'Nhà cung cấp chưa có API key',
  NO_MODEL: 'Nhà cung cấp chưa có model được phép',
  AI_KEY_UNREADABLE: 'Không giải mã được API key, hãy nhập lại key',
};

// The settings part (spec §9.2–§9.3): limits for the company, and each person's own preferences. S8 §8.3: the company
// persona is gone (each person names their own, `/api/assistant/me`); the company carries the scope and gate model.

export const ADDRESS_AS = ['anh', 'chi', 'name', 'custom'] as const;   // 'chi' = "chị"; the web labels it
export type AddressAs = (typeof ADDRESS_AS)[number];
/** Version 0 = never saved: the defaults below, and the first PUT sends `version: 0` (D29). */
export type AssistantSettingsDTO = { scope: AssistantScope; gateModelId: Id | null; dailyQuota: number | null;
  retentionDays: number; defaultModelId: Id | null; fallbackModelId: Id | null;
  disabledUsers: { id: Id; name: string }[]; version: number };
/** A full replace. `disabledUserIds` is the whole list of users the assistant is off for. */
export type AssistantSettingsBody = { version: number; scope: AssistantScope; gateModelId: Id | null;
  dailyQuota: number | null;
  retentionDays: number; defaultModelId: Id | null; fallbackModelId: Id | null; disabledUserIds: Id[] };
/**
 * S8 §8.3 (`GET /api/assistant/me`): the person's own assistant in one DTO under one `version` (0 = no row yet): the
 * chat prefs, their persona (the 029 defaults with no row) and the read-only onboarding state.
 */
export type AssistantMeDTO = { addressAs: AddressAs; addressCustom: string | null; readOnly: boolean;
  reducedMotion: boolean; preferredModelId: Id | null; models: { id: Id; displayName: string }[];
  /** D114: the admin's ingestion switch is on AND a usable default model is set; else the chat hides its file input. */
  ingestEnabled: boolean;
  /** S7 D125: the admin's missions switch (a company with no row: on); off hides the rail row "Nhiệm vụ của Hana". */
  missionsEnabled: boolean;
  personaName: string; selfName: string; appearance: PersonaAppearance; personality: PersonaPersonality;
  instructions: string | null; roleTitle: string | null; focus: PersonaFocus;
  /**
   * Written through `PUT /api/assistant/me/onboarding` under the same version (D113). `steps` are the wizard's visible
   * steps, computed by the server (D115): `[2]` with AI off, `[2,5]` with no usable company default model, else
   * `[2..6]`; `step` is clamped onto them. The client never re-derives the set.
   */
  onboarding: OnboardingState & { steps: OnboardingStep[] };
  /** D114: why the assistant is off for this person, in the server's own words; null = on. */
  aiOff: AssistantOffDTO | null;
  /** D116: the account existed before S8 (029) shipped; such a person may keep everything as it is. */
  existing: boolean;
  /** D117: "Nghe thử thật" samples left today (`VOICE_SAMPLE_DAILY` a business day). */
  voiceSamplesLeft: number;
  version: number };
/** D114: the assistant is off for the person (`user`, the admin's switch) or for everyone (`server`, no secret key). */
export type AssistantOffDTO = { reason: 'user' | 'server'; message: string };
/**
 * D113 `PUT /api/assistant/me/onboarding`, strict, one of three: move to a step (clears "Để sau"), defer at a step,
 * or finish (idempotent). `version` is the `/me` version (0 = no row yet).
 */
export type AssistantOnboardingBody =
  | { version: number; step: OnboardingStep }
  | { version: number; defer: true; step: OnboardingStep }
  | { version: number; complete: true };
/** `PUT /api/assistant/me`: a strict full replace; no onboarding, no display name. */
export type AssistantMeBody = { version: number; addressAs: AddressAs; addressCustom: string | null; readOnly: boolean;
  reducedMotion: boolean; preferredModelId: Id | null; personaName: string; selfName: string;
  appearance: PersonaAppearance; personality: PersonaPersonality; instructions: string | null;
  roleTitle: string | null; focus: PersonaFocus };
/**
 * S8 D119 `POST /api/assistant/me/voice-sample` ("Nghe thử thật"): the persona being edited, unsaved — the strict pick
 * of `AssistantMeBody` the prompt is built from, validated by the same rules, with no `version` (a sample is no write).
 */
export type VoiceSampleBody = Pick<AssistantMeBody, 'addressAs' | 'addressCustom' | 'personaName' | 'selfName'
  | 'personality' | 'instructions' | 'roleTitle'>;
/** D118: the sample's one paragraph and the samples left today (`VOICE_SAMPLE_DAILY` a business day). */
export type VoiceSampleDTO = { text: string; left: number };
/** S7 §5.3: "Luôn hỏi" < "Tự làm việc nhẹ" < "Tự làm trong giới hạn". */
export const AUTONOMY_LEVELS = ['ask', 'light', 'bounded'] as const;
export type AutonomyLevel = (typeof AUTONOMY_LEVELS)[number];
/** S7 §5.5: one outside tool a person lets Hana send by herself. */
export type AutonomyToolRef = { serverId: Id; tool: string };
/**
 * S7 §9.2, §9.5 (D20, D25): the person's autonomy. `financeLimit` is whole đồng as a decimal string (the finance
 * `Money` convention). `version` is `assistant_user_prefs.autonomy_version`, 0 = never saved; the chat prefs keep their
 * own `version`. `ceiling` is the admin's; the effective level is always the lower of the two. `tools` are the person's
 * choices among `availableTools` (confirm tools the admin lets self-send, on an enabled server).
 */
export type AssistantAutonomyDTO = { level: AutonomyLevel; financeLimit: string; tools: AutonomyToolRef[];
  version: number; ceiling: { level: AutonomyLevel; financeMax: string };
  availableTools: { serverId: Id; serverName: string; tool: string; title: string | null }[] };
/** A full replace. Above the ceiling → 422 `AUTONOMY_ABOVE_CEILING`; a tool not in `availableTools` → 422 VALIDATION. */
export type AssistantAutonomyBody = { level: AutonomyLevel; financeLimit: string; tools: AutonomyToolRef[];
  version: number };
/**
 * S7 §9.2 (D19): the company's autonomy ceilings and the missions switch, saved under
 * `assistant_settings.autonomy_version` (0 = never saved), apart from the S1 settings' `version`.
 */
export type AssistantAdminAutonomyDTO = { autonomyMax: AutonomyLevel; autonomyFinanceMax: string;
  autonomyExtDaily: number; missionsEnabled: boolean; version: number };
export type AssistantAdminAutonomyBody = AssistantAdminAutonomyDTO;
/** Where the tokens went (S3 §5.2, L1 D19): a person's chat (`assistant_usage`), or proactive Hana on that person's
 *  behalf (`assistant_proactive_usage`, keyed on the recipient). */
export const AI_USAGE_ORIGINS = ['chat', 'proactive'] as const;
export type AiUsageOrigin = (typeof AI_USAGE_ORIGINS)[number];
export type AiUsageRow = { userId: Id; userName: string; modelKey: string; day: string; inputTokens: number;
  outputTokens: number; origin: AiUsageOrigin };
export type AiUsageDTO = { from: string; to: string; rows: AiUsageRow[] };
/** S8 §8.3: one person's scope-gate counts over the asked window, aggregates only (never a question's text). */
export type AiScopeBlockDTO = { userId: Id; name: string; blocked: number; gateTokens: number };

// The conversation part (spec §6.3, §6.4): tones, turns, messages, confirm cards and the SSE stream.

export const ASSISTANT_TONES = ['happy', 'proud', 'worried', 'surprised', 'confused', 'playful', 'grateful', 'neutral'] as const;
export type AssistantTone = (typeof ASSISTANT_TONES)[number];
export const ASSISTANT_TURN_CODES = ['ASSISTANT_QUOTA', 'TURN_RUNNING', 'PROVIDER_UNAVAILABLE', 'TURN_INTERRUPTED',
  'TURN_TIMEOUT', 'INTERNAL'] as const;
export type AssistantTurnCode = (typeof ASSISTANT_TURN_CODES)[number];
/** `{persona}` is replaced by the persona's name (`turnMessage`). */
export const ASSISTANT_TURN_MESSAGE: Record<AssistantTurnCode, string> = {
  ASSISTANT_QUOTA: 'Bạn đã dùng hết hạn mức Trợ lý AI hôm nay',
  TURN_RUNNING: '{persona} đang trả lời, chờ một chút',
  PROVIDER_UNAVAILABLE: 'Trợ lý AI tạm thời không phản hồi, thử lại sau',
  TURN_INTERRUPTED: 'Lượt bị ngắt do máy chủ khởi động lại, gửi tiếp để {persona} làm nốt',
  TURN_TIMEOUT: 'Lượt chạy quá lâu nên đã dừng, gửi tiếp để {persona} làm nốt',
  INTERNAL: 'Đã có lỗi xảy ra, vui lòng thử lại',
};
export const turnMessage = (code: AssistantTurnCode, persona: string): string =>
  ASSISTANT_TURN_MESSAGE[code].replace('{persona}', persona);
/** P2 review F13 (PO-revisitable): turns running at once per person, so the daily quota caps the cost too. A claim
 *  past it is refused as TURN_RUNNING with its own text. */
export const ASSISTANT_TURNS_PER_USER = 3;
/** The most characters a turn's text may hold (after trimming); the server refuses longer (D148). */
export const TURN_TEXT_MAX = 4000;
export const turnLimitMessage = (persona: string): string =>
  `${persona} đang làm ${ASSISTANT_TURNS_PER_USER} việc cho bạn cùng lúc — đợi một việc xong rồi gửi tiếp nhé`;
export const NO_MODEL_MESSAGE = 'Chưa có model nào được bật cho Trợ lý AI';
/** D124: the last word of a full name ('Trần Bình' → 'Bình'); the prompt's `ADDRESS.name` and the web's `addressOf`
 *  both use it, so Hana addresses the person the same way on both sides. */
export const givenName = (full: string): string => full.trim().split(/\s+/).at(-1) ?? full;

/** `person`: a user id, edited with the app's person picker over every active account (D159). `lines`: read-only
 *  (P4 G3 ruling 2), a list of `{ id, deadline }` shown one line per task as `“title” → dd/MM`, the title from the
 *  object resolver; it never makes a card editable. `tree`: a draft subtree (`ConfirmTreeCard[]`, P4 G4 ruling), shown
 *  read-only as an indented list; an edit may only take cards away (a card with its descendants), never add or change
 *  one, so it has no form row. */
export type ConfirmFieldKind = 'text' | 'textarea' | 'date' | 'select' | 'person' | 'lines' | 'tree' | 'mission'
  | 'missionDiff' | 'order';
export type ConfirmField = { name: string; label: string; kind: ConfirmFieldKind; required: boolean;
  options?: { value: string; label: string }[]; maxLength?: number | null };
/** Whether an edit can change the field (D75): every kind but the read-only `lines` (a `tree` by removal only) and
 *  `missionDiff` (S7 D117: a revise is approved or rejected, never edited). A `mission` is edited on its own card. */
export const isEditableField = (f: Pick<ConfirmField, 'kind'>): boolean => f.kind !== 'lines' && f.kind !== 'missionDiff';
/** Whether `Sửa`'s form has a row for the field: the editable kinds but `tree`, which the map's ghost ✕ edits, and
 *  `mission` and `order`, whose cards edit them in place (S7 D117, P7). */
export const isFormField = (f: Pick<ConfirmField, 'kind'>): boolean =>
  f.kind !== 'lines' && f.kind !== 'tree' && f.kind !== 'mission' && f.kind !== 'missionDiff' && f.kind !== 'order';
/** One row of a `lines` field. */
export type ConfirmLine = { id: Id; deadline: string };
/** One card of a `tree` field, parents before their children (`work.apply_plan`, D196). */
export type ConfirmTreeCard = { key: string; parentKey: string | null; title: string; itemType: 'task' | 'note';
  recipientId: Id | null; deadline: string | null; filled?: ('recipient' | 'deadline')[] };
/** The pages Hana can be told about, longest prefix first; the labels are the navigation's (config/navigation.tsx). */
export const ASSISTANT_PAGES: readonly { prefix: string; label: string }[] = [
  { prefix: '/finance/projects', label: 'Tài chính dự án' }, { prefix: '/finance/entries', label: 'Khoản thu chi' },
  { prefix: '/finance', label: 'Tài chính' }, { prefix: '/workplace', label: 'Workplace' },
  { prefix: '/projects', label: 'Dự án' }, { prefix: '/strategy', label: 'Chiến lược' },
  { prefix: '/partners', label: 'Đối tác' }, { prefix: '/activity', label: 'Nhật ký' },
  { prefix: '/history', label: 'Lịch sử' }, { prefix: '/admin/roles', label: 'Vai trò & quyền' },
  { prefix: '/admin/ai', label: 'Quản trị AI' }, { prefix: '/admin/users', label: 'Quản trị tài khoản' },
  { prefix: '/account', label: 'Tài khoản' },
];
/** The label of a known page; null = not a known page, so the turn carries no page context (D76, D120). */
export function assistantPageLabel(route: string): string | null {
  if (route === '/') return 'Dashboard';
  const hit = ASSISTANT_PAGES.find((x) => route === x.prefix || route.startsWith(`${x.prefix}/`));
  return hit?.label ?? null;
}
/** D178: the known page a route belongs to, as its `ASSISTANT_PAGES` prefix (`/` for the Dashboard); null = unknown. */
export function assistantPagePrefix(route: string): string | null {
  if (route === '/') return '/';
  return ASSISTANT_PAGES.find((x) => route === x.prefix || route.startsWith(`${x.prefix}/`))?.prefix ?? null;
}
/** The Workplace's filter flags (web `FILTER_FLAGS`), and their names as the filter bar says them. */
export const ASSISTANT_FILTER_FLAGS = ['mine', 'blocked', 'late', 'soon', 'week', 'today', 'important', 'changed'] as const;
export type AssistantFilterFlag = (typeof ASSISTANT_FILTER_FLAGS)[number];
export const ASSISTANT_FILTER_LABEL: Readonly<Record<AssistantFilterFlag, string>> = {
  mine: 'Của tôi', blocked: 'Đang vướng', late: 'Quá hạn', soon: 'Sắp đến hạn (3 ngày)', week: 'Tuần này',
  today: 'Hôm nay', important: 'Quan trọng', changed: 'Thay đổi 24h',
};
/**
 * Plan P3 D160: what a page shows besides its route, as typed values parsed from its URL (never free text: the
 * Workplace search box never leaves the browser; `searching` only says one is active, D173). One variant per page
 * kind; the server labels every id through the asker's own sight and drops the ones it does not see.
 */
/** The Workplace views a page may name: the three board views and S9's two delegation lists (L3 T11). */
export const ASSISTANT_WORKPLACE_VIEWS = ['map', 'kanban', 'list', 'delegated', 'assigned'] as const;
export type AssistantWorkplaceView = typeof ASSISTANT_WORKPLACE_VIEWS[number];
export type AssistantPageFacts =
  | { kind: 'dashboard'; mine: boolean }
  | { kind: 'workplace'; view: AssistantWorkplaceView; flags: AssistantFilterFlag[]; recipientId?: Id; projectId?: Id;
      sharing?: SharingMode; treeId?: Id; searching?: true }
  | { kind: 'strategy'; year?: number }
  | { kind: 'finance'; year?: number; month?: string; projectId?: Id; direction?: Direction; status?: EntryStatusFilter }
  | { kind: 'activity'; from?: string; to?: string; actorId?: Id; module?: AuditModule; via?: ViaChannel };
/** The facts kind a route may carry; null = the route carries none (its route and object say it all). */
export function assistantFactsKind(route: string): AssistantPageFacts['kind'] | null {
  switch (route) {
    case '/': return 'dashboard';
    case '/workplace': return 'workplace';
    case '/strategy': return 'strategy';
    case '/finance': case '/finance/entries': return 'finance';
    case '/activity': return 'activity';
    default: return null;
  }
}
export type AssistantPageContext = { route: string; objectRef?: AssistantObjectRef; facts?: AssistantPageFacts };
/** S3 §10.2 (L2 D61, L3 D128): "Hỏi Hana" from a nudge, or "Hỏi Hana thêm" from a briefing. Only an id: the server
 *  builds the context from the owner's own row, never from text the browser sends. Accepted on the first turn of a
 *  conversation only. */
export type AssistantTurnContext = { kind: 'nudge' | 'briefing'; id: Id };
export type AssistantTurnBody = { text: string; page?: AssistantPageContext; context?: AssistantTurnContext };
export type AssistantStep = { callId: string; capability: string; label: string; ok: boolean; errorCode?: string };
export type AssistantMessageDTO = { id: Id; role: 'user' | 'assistant'; content: string; tone: AssistantTone | null;
  objects: AssistantObjectRef[]; steps: AssistantStep[]; createdAt: string;
  /** S5 D53: `batch` is an ingest job's card (`jobId`), drawn by the batch UI, never as a chat bubble. */
  kind: 'text' | 'batch'; jobId: Id | null;
  /** S8 §7.1: a turn the scope gate refused (the question and its refusal). Only its owner ever reads it. */
  blocked: boolean };
export type AssistantPendingDTO = { actionId: Id; capability: string; summary: string; input: Record<string, unknown>;
  fields: ConfirmField[]; expiresAt: string; objects: AssistantObjectRef[];
  /** S6 §7.5: an internal card of a conversation that read outside content names those servers, first read first, for
   *  the line "Hội thoại này có nội dung từ <tên máy chủ>". Absent when the conversation read none. */
  taint?: string[];
  /** S9 §9.2 (D47): an internal card of a conversation that read other people's words (`delegation.thread`) names
   *  them by given name, first read first, for the line "Lượt này đã đọc bình luận của …". Absent when it read none. */
  peerTaint?: string[];
  /** S6 §7.7 (D44): only on an `ext.call` card, whose `fields` is then `[]` (its args are these fields instead). */
  external?: AssistantExternalCardDTO;
  /** S6 G3 F1: the model's call that stopped at this card (an edit keeps it), the call whose `tool_done` the resumed
   *  turn streams for it. Absent on a row stored before 026's `call_id` and on a row no model call made. */
  callId?: string;
  /** S7 D11, D14: why the autonomy verdict made this card wait, for the line the client writes; absent when it was
   *  not autonomy. `names`: the people a `reach_others` card touches, as named in the company (absent when none can be
   *  named → "người khác", D32); `limit`: the finance limit in VND of an `over_limit` card. */
  reason?: AssistantPendingReason };
export const ASSISTANT_PENDING_REASONS = ['level_ask', 'reach_others', 'over_limit', 'tainted'] as const;
export type AssistantPendingReason = { code: (typeof ASSISTANT_PENDING_REASONS)[number]; names?: string[];
  limit?: string };
/**
 * S6 §7.7 (D44): the external confirm card, built when the pending DTO is built from the stored input
 * `{ connectionId, tool, schemaHash, args }` and the catalogue.
 */
export type AssistantExternalCardDTO = {
  /** The server's id, which picks its square's colour as the admin and Kết nối pages do. A soft-deleted server keeps it
   *  (the same colour); null only when the connection row is missing. */
  serverId: Id | null;
  /** The server's name (a deleted server keeps its stored name). */
  serverName: string;
  /** The first letter of `serverName`, uppercased: the card's square. */
  initial: string;
  /** The tool's title as approved (D48), else its name. */
  toolTitle: string;
  /** `send`: "Gửi ra <tên máy chủ>. Không hoàn tác được."; `raised`: a `read` tool raised to a card by §7.4, "Yêu cầu
   *  này gửi dữ liệu tới <tên máy chủ>". Recomputed from the tool's current risk. */
  warn: 'send' | 'raised';
  /** Every top-level arg: the approved schema's `properties` order, then the args keys it does not list (none hidden). */
  fields: ExtCardField[];
  /** Whether `Sửa` can change anything: some field is `editable`. */
  editable: boolean;
};
/** One top-level arg of an external card (§7.7). */
export type ExtCardField = {
  /** The arg's key; `Sửa` sends `{ input: { args: { [key]: value } } }`. */
  key: string;
  /** The schema property's `title`, else the key (always the key when the approved schema is no longer the card's). */
  label: string;
  /** `tree`: an array or nested object (or `null`), shown as a read-only tree. */
  kind: 'string' | 'number' | 'integer' | 'boolean' | 'enum' | 'tree';
  /** The arg's value as stored. */
  value: unknown;
  /** Only a top-level string/number/integer/boolean/enum the approved schema declares. */
  editable: boolean;
  /** The schema's `enum` values, for an `enum` field. */
  options?: (string | number | boolean)[];
};
/**
 * S6 D47: the confirm route's JSON answer for an `ext.call` row that did not resume a turn (a second click, a replay):
 * `sending` (still out), `executed` ("Đã gửi"), `failed` with its code (`EXT_UNKNOWN`: "Không chắc đã gửi").
 */
export type AssistantExtConfirmDTO = { actionId: Id } & (
  | { status: 'sending' } | { status: 'executed' } | { status: 'failed'; errorCode: string });
/**
 * One command's undo (spec §5.4 "Nhóm Hoàn tác", D187): the executed, undoable actions of one turn. `actionId` is its
 * newest member (any member undoes the whole group), `groupId` its first, `count` how many; `summary` is
 * `groupSummary` of the members; `expiresAt` the earliest member's end, when the whole group's window ends.
 */
export type AssistantUndoableDTO = { actionId: Id; groupId: Id; count: number; summary: string; expiresAt: string };

/** D188: one member keeps its summary; several name the first change and count the rest. */
export function groupSummary(summaries: readonly string[]): string {
  const [first = ''] = summaries;
  return summaries.length > 1 ? `${first} và ${summaries.length - 1} thay đổi khác` : first;
}
export type AssistantConversationSummaryDTO = { id: Id; title: string; lastMessageAt: string | null; updatedAt: string };
export type AssistantConversationDTO = { id: Id; title: string; messages: AssistantMessageDTO[]; hasOlder: boolean;
  pending: AssistantPendingDTO[]; undoable: AssistantUndoableDTO[]; running: boolean };
/** One row per asked ref, echoing its `type` and `id`. For a `task`, `note` or `work` ref, `itemType` is the item's real
 *  type, null when it is hidden or missing (the two stay identical, D77); the other types have no `itemType`. */
export type AssistantResolvedObject<T extends AssistantLinkType = AssistantLinkType> = { type: T; id: Id;
  label: string | null; href: string | null; itemType?: 'task' | 'note' | null };
export type AssistantResolveBody = { refs: AssistantLinkRef[] };
export type AssistantEditBody = { input: Record<string, unknown> };
export type AssistantSseEvent =
  | { type: 'turn_started'; data: { turnId: Id; conversationId: Id } }
  | { type: 'thinking'; data: Record<string, never> }
  | { type: 'tool_started'; data: { callId: string; capability: string; label: string } }
  | { type: 'tool_done'; data: { callId: string; ok: boolean; errorCode?: string; objects: AssistantObjectRef[] } }
  | { type: 'confirm_required'; data: AssistantPendingDTO & { callId: string } }
  | { type: 'message_delta'; data: { text: string } }
  | { type: 'message_done'; data: { messageId: Id | null; text: string; tone: AssistantTone | null;
      /** S8 §7.1: present only on the scope gate's refusal. */
      blocked?: true } }
  | { type: 'undo_available'; data: AssistantUndoableDTO }
  | { type: 'error'; data: { code: AssistantTurnCode; message: string } }
  | { type: 'turn_done'; data: Record<string, never> };
export type AssistantSseType = AssistantSseEvent['type'];
